/**
 * User <-> session index at `users/{uid}/sessions/{eventId}`.
 *
 * Metadata only: event id, title, role and timestamps. Event documents are
 * never duplicated here and never deleted, so history stays cheap to read and
 * the event remains the single source of truth.
 */
import {
  collection,
  deleteField,
  doc,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
} from 'firebase/firestore';
import { db } from '../firebase';
import {
  applyEventLifecycleToSession,
  buildSessionEntry,
  buildSessionTouch,
  deriveSessionStatus,
  isIntentionalLeave,
  sessionLifecycleFields,
  SESSION_MEMBERSHIP,
  SESSION_STATUS,
} from '../utils/sessionHistory';
import { describeError } from '../utils/errorLog';

const HISTORY_PAGE_SIZE = 50;

function sessionsCollection(uid) {
  return collection(db, 'users', uid, 'sessions');
}

function sessionRef(uid, eventId) {
  return doc(db, 'users', uid, 'sessions', eventId);
}

export async function fetchSessionEntry(uid, eventId) {
  if (!uid || !eventId) return null;
  try {
    const snapshot = await getDoc(sessionRef(uid, eventId));
    if (!snapshot?.exists?.()) return null;
    return { eventId: snapshot.id, ...snapshot.data() };
  } catch (error) {
    console.error('Failed to read session membership:', describeError(error));
    return null;
  }
}

/**
 * Record (or refresh) that this uid took part in an event.
 * Idempotent: the event id is the document id, so rejoining only touches
 * `lastSeenAt` instead of creating duplicates.
 */
export async function recordSessionMembership({
  uid,
  eventId,
  title = '',
  role = 'guest',
  createdAt = null,
  endedAt = null,
  status = null,
  leftAt = null,
  clearLeave = false,
} = {}) {
  if (!uid || !eventId) return null;

  try {
    const ref = sessionRef(uid, eventId);
    const existing = await getDoc(ref);
    const timestamp = serverTimestamp();
    const lifecycle = sessionLifecycleFields({ status, endedAt });

    const payload = existing?.exists?.()
      ? buildSessionTouch({ title, role, timestamp, leftAt, ...lifecycle, status: status ?? lifecycle.status })
      : buildSessionEntry({
        eventId,
        title,
        role,
        createdAt,
        timestamp,
        leftAt,
        ...lifecycle,
        status: status ?? lifecycle.status,
      });

    if (!payload) return null;

    if (clearLeave) {
      payload.status = deleteField();
      payload.leftAt = deleteField();
    }

    await setDoc(ref, payload, { merge: true });
    return payload;
  } catch (error) {
    console.error('Failed to record session membership:', describeError(error));
    return null;
  }
}

export async function markSessionLeft({ uid, eventId, title = '', role = 'guest' } = {}) {
  const leftAt = Date.now();
  return recordSessionMembership({
    uid,
    eventId,
    title,
    role,
    status: SESSION_MEMBERSHIP.left,
    leftAt,
  });
}

async function lookupEventById(eventId) {
  if (!eventId) return null;
  try {
    const snapshot = await getDoc(doc(db, 'events', eventId));
    if (!snapshot?.exists?.()) return null;
    return { id: snapshot.id, ...snapshot.data() };
  } catch (error) {
    console.error('Failed to read event for session history:', describeError(error));
    return null;
  }
}

/**
 * Guests who were offline when a party ended still have an "active" private
 * index row. Host cannot write that row (owner-only), so on load we fetch
 * each still-active-looking event by id and overlay `status` / `endedAt`.
 * Events stay unlistable; only known ids are read.
 */
async function reconcileLoadedSessions(uid, sessions) {
  const now = Date.now();
  const stale = sessions.filter((entry) => {
    if (entry.endedAt || entry.status === 'ended') return false;
    if (deriveSessionStatus(entry, { now }) === SESSION_STATUS.active) return true;
    return isIntentionalLeave(entry);
  });
  if (stale.length === 0) return sessions;

  const events = await Promise.all(
    stale.map(async (entry) => [entry.eventId, await lookupEventById(entry.eventId)]),
  );
  const byId = Object.fromEntries(events);

  const reconciled = sessions.map((entry) => {
    if (!Object.prototype.hasOwnProperty.call(byId, entry.eventId)) return entry;
    return applyEventLifecycleToSession(entry, byId[entry.eventId]);
  });

  reconciled
    .filter((entry) => entry.status === 'ended' || entry.endedAt)
    .filter((entry) => stale.some((row) => row.eventId === entry.eventId && !row.endedAt && row.status !== 'ended'))
    .forEach((entry) => {
      setDoc(sessionRef(uid, entry.eventId), {
        status: 'ended',
        endedAt: entry.endedAt ?? Date.now(),
      }, { merge: true }).catch((error) => {
        console.error('Failed to persist ended session overlay:', describeError(error));
      });
    });

  return reconciled;
}

export async function fetchUserSessions(uid, { pageSize = HISTORY_PAGE_SIZE } = {}) {
  if (!uid) return [];

  try {
    const snapshot = await getDocs(
      query(sessionsCollection(uid), orderBy('lastSeenAt', 'desc'), limit(pageSize)),
    );
    const docs = snapshot?.docs ?? [];
    const sessions = docs.map((entry) => ({ eventId: entry.id, ...entry.data() }));
    return reconcileLoadedSessions(uid, sessions);
  } catch (error) {
    console.error('Failed to load session history:', describeError(error));
    return [];
  }
}
